import { type Component, createMemo, For, getOwner, runWithOwner, Show } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { useCustomerState } from "../../../state/customer";
import UserService from "../../../services/UserService";
import { isEmpty, isNil, orderBy, partition, sortBy } from "lodash-es";
import Text from "../../../components/text/Text";
import { useTranslation } from "../../../state/i18n";
import {
  filterCustomRoles,
  getRoleDescription,
  getRoleName,
  ROLE_SCOPE_TRANSLATION_KEYS,
} from "../../../utils/roleUtil";
import { IconButtonMenu } from "../../../components/listItemMenu/ListItemMenu";
import { IconButton, TextAndIconButton } from "../../../components/button/Button";
import { useModalManager } from "../../../components/modal/ModalManager";
import DeleteRoleConfirmationModal from "./DeleteRoleConfirmationModal";
import DeleteRoleInUseModal from "./DeleteRoleInUseModal";
import ViewRoleModal from "./ViewRoleModal";
import FeatureService from "../../../services/FeatureService";
import type { SystemRoleType } from "../../../state/role";
import { MAX_CUSTOM_ROLES } from "../../../constants";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { Icon } from "../../../components/icon/Icon";
import UpsertRoleModal from "./UpsertRoleModal.tsx";

const SYSTEM_ROLE_ORDER: SystemRoleType[] = [
  "ACCOUNT_OWNER",
  "ACCOUNT_ADMINISTRATOR",
  "USER",
  "FOLDER_MANAGER",
  "FORM_MANAGER",
  "FORM_USER",
];

const RolesPage: Component<RouteSectionProps<never>> = () => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();

  const rolesQuery = UserService.getRoles(customer.customerId);
  const hasReachedCustomRolesLimit = createMemo(() =>
    rolesQuery.status === "success" ? filterCustomRoles(rolesQuery.data).length >= MAX_CUSTOM_ROLES : false,
  );

  const orderedRoles = createMemo(() => {
    if (isNil(rolesQuery.data)) {
      return;
    }

    const [systemRoles, customRoles] = partition(rolesQuery.data, (role) => role.system);
    const sortedSystemRoles = sortBy(systemRoles, (role) => SYSTEM_ROLE_ORDER.indexOf(role.type));
    const sortedCustomRoles = orderBy(customRoles, "createdOn", "desc");

    return [...sortedCustomRoles, ...sortedSystemRoles];
  });

  return (
    <div class="flex h-full flex-col gap-6">
      <div class="flex justify-between pb-3">
        <Text text={t(($) => $.ROLES_PAGE_TITLE)} as="h1" class="text-header-xl text-primary" />
        <Show
          when={
            FeatureService.hasFeature("ADVANCED_USER_MANAGEMENT") &&
            !isEmpty(rolesQuery.data) &&
            !hasReachedCustomRolesLimit()
          }
        >
          <TextAndIconButton
            variant="light"
            size="large"
            text={t(($) => $.ROLES_PAGE_ACTION_CREATE_ROLE_LABEL)}
            icon="plus"
            onClick={() => {
              open({ content: () => <UpsertRoleModal /> });
            }}
          />
        </Show>
      </div>

      <Show when={hasReachedCustomRolesLimit()}>
        <InlineMessage
          variant="warning"
          label={t(($) => $.ROLES_PAGE_CUSTOM_ROLES_LIMIT_REACHED, { limit: MAX_CUSTOM_ROLES })}
        />
      </Show>

      <div class="relative h-full min-h-0" aria-busy={rolesQuery.isFetching}>
        <Show
          when={orderedRoles()}
          fallback={
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">{t(($) => $.ROLES_PAGE_LOADING_TITLE)}</span>
            </div>
          }
        >
          {(roles) => (
            <div class="h-full">
              <div
                role="table"
                style={{
                  "--column-template": "1fr 1fr 1fr 1fr 4rem",
                }}
                class="flex size-full border-collapse flex-col overflow-hidden"
              >
                <div role="rowgroup">
                  <div
                    role="row"
                    class="grid grid-cols-template gap-3 border-b border-subtle text-base-semibold text-secondary"
                  >
                    <div role="columnheader" class="py-1.5">
                      {t(($) => $.ROLES_PAGE_COLUMN_TITLE)}
                    </div>
                    <div role="columnheader" class="py-1.5">
                      {t(($) => $.ROLES_PAGE_COLUMN_DESCRIPTION)}
                    </div>
                    <div role="columnheader" class="py-1.5">
                      {t(($) => $.ROLES_PAGE_COLUMN_SCOPE)}
                    </div>
                    <div role="columnheader" class="py-1.5">
                      {t(($) => $.ROLES_PAGE_COLUMN_TYPE)}
                    </div>
                    <div role="columnheader" class="py-1.5">
                      {t(($) => $.ROLES_PAGE_COLUMN_ACTIONS)}
                    </div>
                  </div>
                </div>
                <div role="rowgroup" class="h-full divide-y divide-subtle overflow-y-auto pb-6">
                  <For each={roles()}>
                    {(role) => (
                      <div
                        role="row"
                        class="grid grid-cols-template items-center gap-3 text-base-regular text-secondary"
                      >
                        <div role="cell" class="flex min-w-0 py-4">
                          <Text text={getRoleName(role, t)} truncate />
                        </div>
                        <div role="cell" class="flex min-w-0 py-4">
                          <Text text={getRoleDescription(role, t) ?? ""} truncate={4} />
                        </div>
                        <div role="cell" class="py-4">
                          {t(ROLE_SCOPE_TRANSLATION_KEYS[role.scope])}
                        </div>
                        <div role="cell" class="py-4">
                          <Show when={role.system} fallback={t(($) => $.ROLES_PAGE_COLUMN_TYPE_CUSTOM)}>
                            {t(($) => $.ROLES_PAGE_COLUMN_TYPE_DEFAULT)}
                          </Show>
                        </div>
                        <div role="cell" class="py-4">
                          <Show
                            when={role.system}
                            fallback={
                              <IconButtonMenu
                                items={[
                                  [
                                    role.scope === "MIXED"
                                      ? {
                                          variant: "icon",
                                          type: "action",
                                          value: "view",
                                          icon: { name: "eye", type: "outline" },
                                          label: t(($) => $.ROLES_PAGE_COLUMN_ACTIONS_VIEW),
                                          onClick: () => {
                                            open({ content: () => <ViewRoleModal role={role} /> });
                                          },
                                        }
                                      : {
                                          variant: "icon",
                                          type: "action",
                                          value: "edit",
                                          icon: { name: "pencil", type: "outline" },
                                          label: t(($) => $.ROLES_PAGE_COLUMN_ACTIONS_EDIT),
                                          onClick: () => {
                                            open({ content: () => <UpsertRoleModal role={role} /> });
                                          },
                                        },
                                    {
                                      variant: "icon",
                                      type: "action",
                                      value: "delete",
                                      icon: { name: "trash", type: "outline" },
                                      label: t(($) => $.ROLES_PAGE_COLUMN_ACTIONS_DELETE),
                                      onClick: () => {
                                        runWithOwner(owner, async () => {
                                          const [usersByRole, groupsByRole, invitesByRole] = await Promise.all([
                                            UserService.getUsersByRole(customer.customerId, role.id),
                                            UserService.getGroupsByRole(customer.customerId, role.id),
                                            UserService.getInvitesByRole(customer.customerId, role.id),
                                          ]);

                                          open({
                                            content: () =>
                                              isEmpty(usersByRole) &&
                                              isEmpty(groupsByRole) &&
                                              isEmpty(invitesByRole) ? (
                                                <DeleteRoleConfirmationModal
                                                  customerId={customer.customerId}
                                                  roleId={role.id}
                                                />
                                              ) : (
                                                <DeleteRoleInUseModal
                                                  users={usersByRole}
                                                  groups={groupsByRole}
                                                  invites={invitesByRole}
                                                />
                                              ),
                                          });
                                        });
                                      },
                                    },
                                  ],
                                ]}
                                aria-label={t(($) => $.ACTIONS_ARIA)}
                              />
                            }
                          >
                            <IconButton
                              icon="eye"
                              aria-label={t(($) => $.ROLES_PAGE_COLUMN_ACTIONS_DETAIL_ARIA)}
                              variant="tertiary"
                              size="medium"
                              onClick={() => {
                                open({ content: () => <ViewRoleModal role={role} /> });
                              }}
                            />
                          </Show>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

const ProtectedRolesPage = withPagePermissionCheck(RolesPage, PermissionService.canManagePermissions);

export default ProtectedRolesPage;
